import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { api } from "../api/client";
import type { MarketRow, Meta } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { useMutation, useResource } from "../hooks/useResource";
import {
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  LoadingState,
  Mono,
  PageHeader,
  RoleGate,
} from "../components/common";

/**
 * Markets: the (region, industry) unit that the client cap and routing strategy
 * hang off. `seatsLeft` is the number that decides whether onboarding the next
 * client is possible at all, so it is a column rather than something to work out.
 */

interface RosterRow {
  market: string;
  industry: string;
  client_cap: string;
  routing_strategy: string;
  client: string;
  active: string;
  quota: { perDay?: number } | null;
  zones: string | null;
  assigned_24h: number;
}

interface Draft {
  key: string;
  region: string;
  industry: string;
  clientCap: string;
  routingStrategy: string;
}

const emptyDraft = (): Draft => ({
  key: "",
  region: "",
  industry: "",
  clientCap: "5",
  routingStrategy: "geo_exclusive",
});

export function MarketsPage() {
  const { can } = useAuth();
  const markets = useResource<{ items: MarketRow[] }>("/api/markets");
  const roster = useResource<{ items: RosterRow[] }>("/api/markets/roster");
  const meta = useResource<Meta>("/api/meta");

  const [editing, setEditing] = useState<MarketRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<MarketRow | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const remove = useMutation(async (market: MarketRow) => {
    await api.del(`/api/markets/${market.id}`);
  });

  if (markets.loading && !markets.data) return <LoadingState label="Loading markets…" />;

  return (
    <Box>
      <PageHeader
        title="Markets"
        subtitle="A capped (region, industry) territory. The cap is what stops a territory being oversold."
        actions={
          <RoleGate allowed={can("operator")} needs="operator">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
              Add market
            </Button>
          </RoleGate>
        }
      />

      <ErrorBanner error={markets.error} onRetry={markets.reload} />
      <ErrorBanner error={remove.error} />

      {markets.data && markets.data.items.length === 0 ? (
        <EmptyState title="No markets configured" hint="A signal needs a market before it can produce leads." />
      ) : null}

      {markets.data && markets.data.items.length > 0 ? (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Industry</TableCell>
                <TableCell>Routing</TableCell>
                <TableCell align="right">Clients</TableCell>
                <TableCell align="right">Cap</TableCell>
                <TableCell align="right">Seats left</TableCell>
                <TableCell align="right">Signals</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {markets.data.items.map((market) => (
                <TableRow key={market.id} hover>
                  <TableCell>
                    <Mono>{market.key}</Mono>
                  </TableCell>
                  <TableCell>{market.region}</TableCell>
                  <TableCell>{market.industry}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2">{market.routingStrategy}</Typography>
                      {(meta.data?.routingNotImplemented ?? []).includes(market.routingStrategy) ? (
                        <Tooltip title="Declared in the spec but not implemented — routing falls back to round_robin.">
                          <Chip label="falls back" color="warning" variant="outlined" />
                        </Tooltip>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{market.activeClients}</TableCell>
                  <TableCell align="right">{market.clientCap ?? "—"}</TableCell>
                  <TableCell align="right">
                    {market.seatsLeft === null ? (
                      "—"
                    ) : (
                      <Chip
                        label={market.seatsLeft}
                        color={market.seatsLeft === 0 ? "error" : "success"}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">{market.signalCount}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <RoleGate allowed={can("operator")} needs="operator">
                        <IconButton onClick={() => setEditing(market)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </RoleGate>
                      <RoleGate allowed={can("owner")} needs="owner">
                        <IconButton color="error" onClick={() => setDeleting(market)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </RoleGate>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : null}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Roster
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Who owns what, and how much of their quota went out in the last 24 hours.
      </Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Market</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Zones</TableCell>
              <TableCell align="right">Daily quota</TableCell>
              <TableCell align="right">Assigned 24h</TableCell>
              <TableCell>Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(roster.data?.items ?? []).map((row, i) => (
              <TableRow key={`${row.market}-${row.client}-${i}`}>
                <TableCell>
                  <Mono>{row.market}</Mono>
                </TableCell>
                <TableCell>{row.client}</TableCell>
                <TableCell>
                  {row.zones ? (
                    <Mono>{row.zones}</Mono>
                  ) : row.routing_strategy === "geo_exclusive" ? (
                    <Tooltip title="On a geo-exclusive market a client with no zones is never assigned anything.">
                      <Chip label="no zones" color="error" variant="outlined" />
                    </Tooltip>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell align="right">{row.quota?.perDay ?? "—"}</TableCell>
                <TableCell align="right">
                  {row.assigned_24h}
                  {row.quota?.perDay ? ` / ${row.quota.perDay}` : ""}
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.active === "true" ? "active" : "paused"}
                    color={row.active === "true" ? "success" : "default"}
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
            {(roster.data?.items ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>No client is subscribed to any market yet.</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>

      {creating || editing ? (
        <MarketDialog
          market={editing}
          strategies={meta.data?.routingStrategies ?? ["geo_exclusive", "round_robin"]}
          notImplemented={meta.data?.routingNotImplemented ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(message) => {
            setCreating(false);
            setEditing(null);
            setToast(message);
            markets.reload();
            roster.reload();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete market ${deleting?.key}?`}
        pending={remove.pending}
        body="Only possible while no client is subscribed and no signal references it."
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          void remove.run(deleting).then((ok) => {
            if (!ok) return;
            setDeleting(null);
            setToast(`Deleted ${deleting.key}`);
            markets.reload();
          });
        }}
      />

      <Snackbar open={toast !== null} autoHideDuration={4000} onClose={() => setToast(null)} message={toast ?? ""} />
    </Box>
  );
}

function MarketDialog({
  market,
  strategies,
  notImplemented,
  onClose,
  onSaved,
}: {
  market: MarketRow | null;
  strategies: string[];
  notImplemented: string[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [draft, setDraft] = useState<Draft>(emptyDraft());

  useEffect(() => {
    if (!market) {
      setDraft(emptyDraft());
      return;
    }
    setDraft({
      key: market.key,
      region: market.region,
      industry: market.industry,
      clientCap: String(market.clientCap ?? 5),
      routingStrategy: market.routingStrategy,
    });
  }, [market]);

  const save = useMutation(async () => {
    const body = {
      key: draft.key.trim(),
      region: draft.region.trim(),
      industry: draft.industry.trim(),
      clientCap: Number(draft.clientCap) || 1,
      routingStrategy: draft.routingStrategy,
    };
    if (market) await api.put(`/api/markets/${market.id}`, body);
    else await api.post("/api/markets", body);
  });

  const submittable =
    draft.key.trim() !== "" && draft.region.trim() !== "" && draft.industry.trim() !== "" && !save.pending;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{market ? `Edit ${market.key}` : "Add market"}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Key"
            value={draft.key}
            onChange={(e) => setDraft({ ...draft, key: e.target.value })}
            helperText="Lowercase with underscores, e.g. boston_roofing"
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Region"
              value={draft.region}
              onChange={(e) => setDraft({ ...draft, region: e.target.value })}
              helperText="e.g. MA, CT, NYC"
              fullWidth
            />
            <TextField
              label="Industry"
              value={draft.industry}
              onChange={(e) => setDraft({ ...draft, industry: e.target.value })}
              helperText="e.g. ROOFING"
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Client cap"
              value={draft.clientCap}
              onChange={(e) => setDraft({ ...draft, clientCap: e.target.value.replace(/\D/g, "") })}
              helperText="Subscribing past this is refused, not silently allowed."
              sx={{ width: 160 }}
            />
            <TextField
              select
              label="Routing strategy"
              value={draft.routingStrategy}
              onChange={(e) => setDraft({ ...draft, routingStrategy: e.target.value })}
              fullWidth
            >
              {strategies.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                  {notImplemented.includes(s) ? " (falls back to round_robin)" : ""}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          {draft.routingStrategy === "geo_exclusive" ? (
            <Alert severity="info">
              Geo-exclusive is strict: a lead in a zone nobody owns is held, never handed to another client.
              Give each subscription its zones.
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!submittable}
          onClick={() =>
            void save
              .run(undefined)
              .then((ok) => ok && onSaved(market ? `Saved ${draft.key}` : `Created ${draft.key}`))
          }
        >
          {save.pending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
